import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatPrice } from "@/lib/api";
import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n/I18nContext";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tag, LineChart, Store, ClipboardList, ShoppingCart, MapPin, TrendingDown, Flame, Sparkles, Newspaper, Lightbulb, ArrowRight,
} from "lucide-react";

const Stat = ({ Icon, label, value, tone = "primary" }) => (
  <div className="rounded-2xl bg-card border border-border p-4" data-testid={`market-stat-${label}`}>
    <span className={`h-9 w-9 rounded-xl flex items-center justify-center ${tone === "success" ? "bg-success/10 text-success" : "bg-primary/10 text-primary"}`}>
      <Icon className="h-5 w-5" />
    </span>
    <p className="mt-2.5 font-heading font-extrabold text-2xl text-foreground">{value}</p>
    <p className="text-xs text-muted-foreground">{label}</p>
  </div>
);

const Sparkline = ({ points }) => {
  if (!points || points.length < 2) return null;
  const max = Math.max(...points), min = Math.min(...points);
  const range = max - min || 1;
  const w = 220, h = 48;
  const step = w / (points.length - 1);
  const d = points.map((p, i) => `${i === 0 ? "M" : "L"} ${i * step} ${h - ((p - min) / range) * h}`).join(" ");
  const up = points[points.length - 1] >= points[0];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-14" preserveAspectRatio="none">
      <path d={d} fill="none" stroke={up ? "hsl(var(--success))" : "hsl(var(--destructive))"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

export const MarketOverview = () => {
  const { t } = useT();
  const brand = useBrand();
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/market/overview").then((r) => setD(r.data)).catch(() => {}); }, []);

  if (!d) return <section className="container-page pt-8"><Skeleton className="h-64 rounded-3xl" /></section>;

  return (
    <section className="container-page pt-8" data-testid="market-overview">
      <div className="flex items-end justify-between gap-3 mb-5">
        <div>
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-foreground">{t("market.title")}</h2>
          <p className="text-muted-foreground mt-1.5">{t("market.subtitle")}</p>
        </div>
        <span className="hidden sm:inline text-[11px] font-semibold px-2.5 py-1 rounded-full bg-muted text-muted-foreground">{t("market.simulated")}</span>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat Icon={Store} label={t("market.offers")} value={d.offers_count} />
        <Stat Icon={ClipboardList} label={t("market.requests")} value={d.requests_count} />
        <Stat Icon={ShoppingCart} label={t("market.transactions")} value={d.transactions_count} tone="success" />
        <Stat Icon={LineChart} label={t("market.activity")} value={d.offers_count + d.requests_count} tone="success" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mt-4">
        {/* Activity curve (no amounts) */}
        <div className="rounded-2xl bg-card border border-border p-5 lg:col-span-2" data-testid="market-activity-curve">
          <div className="flex items-center justify-between">
            <p className="font-heading font-semibold text-foreground flex items-center gap-2"><LineChart className="h-4 w-4 text-primary" /> {t("market.activityCurve")}</p>
            <span className="text-xs text-muted-foreground">{t("market.trend")}</span>
          </div>
          <Sparkline points={d.price_trend} />
          <p className="text-xs text-muted-foreground mt-3">{t("market.activityHint")}</p>
        </div>

        {/* Journal + tip */}
        <div className="grid gap-4">
          <div className="rounded-2xl bg-secondary text-secondary-foreground p-5" data-testid="market-journal">
            <p className="font-heading font-semibold flex items-center gap-2"><Newspaper className="h-4 w-4" /> {t("market.journal")}</p>
            <p className="text-xs text-secondary-foreground/70 mt-2 uppercase tracking-wide">{d.journal.current === "morning" ? t("market.morning") : t("market.evening")}</p>
            <p className="text-sm mt-1 text-secondary-foreground/90">{d.journal.current === "morning" ? d.journal.morning : d.journal.evening}</p>
          </div>
          <div className="rounded-2xl bg-accent/40 border border-border p-5" data-testid="market-tip">
            <p className="font-heading font-semibold text-foreground flex items-center gap-2"><Lightbulb className="h-4 w-4 text-primary" /> {t("market.tip")}</p>
            <p className="text-sm mt-1.5 text-foreground/80">{d.tip_of_day}</p>
          </div>
        </div>
      </div>

      {/* Regions + categories */}
      <div className="grid md:grid-cols-3 gap-4 mt-4">
        <div className="rounded-2xl bg-card border border-border p-5" data-testid="market-high-demand">
          <p className="font-heading font-semibold text-foreground flex items-center gap-2 mb-3"><Flame className="h-4 w-4 text-primary" /> {t("market.highDemand")}</p>
          {d.regions_high_demand.length ? d.regions_high_demand.map((r) => (
            <div key={r.region} className="flex items-center justify-between py-1.5 text-sm border-b border-border/50 last:border-0">
              <span className="flex items-center gap-1.5 text-foreground"><MapPin className="h-3.5 w-3.5 text-muted-foreground" /> {r.region}</span>
              <span className="font-semibold text-primary">{r.count}</span>
            </div>
          )) : <p className="text-sm text-muted-foreground">{t("market.noData")}</p>}
        </div>
        <div className="rounded-2xl bg-card border border-border p-5" data-testid="market-deficit">
          <p className="font-heading font-semibold text-foreground flex items-center gap-2 mb-3"><TrendingDown className="h-4 w-4 text-destructive" /> {t("market.deficit")}</p>
          {d.regions_deficit.length ? d.regions_deficit.map((r) => (
            <div key={r.region} className="flex items-center justify-between py-1.5 text-sm border-b border-border/50 last:border-0">
              <span className="flex items-center gap-1.5 text-foreground"><MapPin className="h-3.5 w-3.5 text-muted-foreground" /> {r.region}</span>
              <span className={`font-semibold ${r.gap > 0 ? "text-destructive" : "text-muted-foreground"}`}>{r.gap > 0 ? `+${r.gap}` : r.gap}</span>
            </div>
          )) : <p className="text-sm text-muted-foreground">{t("market.noData")}</p>}
        </div>
        <div className="rounded-2xl bg-card border border-border p-5" data-testid="market-top-categories">
          <p className="font-heading font-semibold text-foreground flex items-center gap-2 mb-3"><Sparkles className="h-4 w-4 text-primary" /> {t("market.topCategories")}</p>
          {d.top_categories.length ? d.top_categories.map((c) => (
            <div key={c.category} className="flex items-center justify-between py-1.5 text-sm border-b border-border/50 last:border-0">
              <span className="text-foreground">{t(`categories.${c.category}`)}</span>
              <span className="font-semibold text-primary">{c.count}</span>
            </div>
          )) : <p className="text-sm text-muted-foreground">{t("market.noData")}</p>}
        </div>
      </div>

      {/* Opportunities */}
      {d.opportunities.length > 0 && (
        <div className="mt-4" data-testid="market-opportunities">
          <p className="font-heading font-semibold text-foreground flex items-center gap-2 mb-3"><Sparkles className="h-4 w-4 text-primary" /> {t("market.opportunities")}</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {d.opportunities.map((o) => (
              <Link key={o.id} to={`/annonce/${o.id}`} className="group rounded-2xl bg-card border border-border overflow-hidden hover:shadow-md transition-shadow">
                <div className="h-24 bg-muted overflow-hidden">
                  <img src={o.image} alt="" className="h-full w-full object-cover group-hover:scale-105 transition-transform" />
                </div>
                <div className="p-3">
                  <p className="text-sm font-semibold text-foreground truncate">{o.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t(`categories.${o.category}`)}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
